import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faSliders } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../Animate/Animate"
import Button, { ButtonIcon } from "../../Button/Button"
import { TitlePageResponsiveIconSmall } from "../../Titles/Titles"
import { SimpleToggle } from "../../SimpleToggle/SimpleToggle"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { SystemModuleType } from "../../../Enum/SystemModuleType"
import { useCompanyModulesStatus } from "../../../context/useCompanyModulesStatusContext"
import { useUpdateCompanyModules } from "../../../hooks/CompanyModules/useUpdateCompanyModules"
import { useError } from "../../../context/useErrorContext"
import { PopupHelpButton } from "../../Help/PopupHelpButton"

type ModuleDisplayGroup = {
  titleKey: string
  modules: SystemModuleType[]
}

const ACTIVE_CONFIGURATION_LOCK = "ActiveConfigurationLock"

const MODULE_DISPLAY_GROUPS: ModuleDisplayGroup[] = [
  {
    titleKey: "companyModules.groups.orders",
    modules: [SystemModuleType.ReservedOrders, SystemModuleType.CustomerExitCheck],
  },
  { titleKey: "companyModules.groups.reservations", modules: [SystemModuleType.Reservations] },
  { titleKey: "companyModules.groups.timeClock", modules: [SystemModuleType.TimeClock] },
  { titleKey: "companyModules.groups.checklists", modules: [SystemModuleType.Checklists] },
  { titleKey: "companyModules.groups.ai", modules: [SystemModuleType.AiAgents] },
  { titleKey: "companyModules.groups.fiscal", modules: [SystemModuleType.FiscalIntegration] },
  { titleKey: "companyModules.groups.delivery", modules: [SystemModuleType.Delivery] },
  {
    titleKey: "companyModules.groups.whatsapp",
    modules: [
      SystemModuleType.WhatsAppAutomation,
      SystemModuleType.WhatsAppCrm,
      SystemModuleType.WhatsAppOfficial,
      SystemModuleType.RespostaAi,
    ],
  },
  {
    titleKey: "companyModules.groups.services",
    modules: [
      SystemModuleType.Promotions,
      SystemModuleType.Awards,
      SystemModuleType.Coupons,
      SystemModuleType.Notices,
      SystemModuleType.ExtraServices,
    ],
  },
  {
    titleKey: "companyModules.groups.company",
    modules: [
      SystemModuleType.StoreGroups,
      SystemModuleType.PushNotifications,
      SystemModuleType.PrintAutomation,
      SystemModuleType.ManualExternalOrders,
    ],
  },
]

type CompanyModulesPopupProps = {
  open: boolean
  onClose: () => void
}

export const CompanyModulesPopup: React.FC<CompanyModulesPopupProps> = ({ open, onClose }) => {
  const { t } = useTranslation("pages")

  const { statuses, isLoading, refresh } = useCompanyModulesStatus()

  const { handleUpdateModules } = useUpdateCompanyModules()

  const { setError } = useError()

  const [pendingDisabled, setPendingDisabled] = useState<string[]>([])

  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!open) return

    void refresh()
  }, [open, refresh])

  useEffect(() => {
    setPendingDisabled(statuses.filter((status) => !status.isEnabled).map((status) => status.code))
  }, [statuses])

  if (!open) return null

  const isPendingEnabled = (moduleCode: SystemModuleType) => !pendingDisabled.includes(moduleCode)

  const lockedByFor = (moduleCode: SystemModuleType) =>
    statuses.find((status) => status.code === moduleCode)?.isLockedBy ?? []

  const describeLock = (lockCode: string) =>
    lockCode === ACTIVE_CONFIGURATION_LOCK
      ? t("companyModules.lockedByActiveConfiguration")
      : t(`companyModules.modules.${lockCode}.title`)

  const handleToggle = (moduleCode: SystemModuleType) => {
    setPendingDisabled((current) =>
      current.includes(moduleCode) ? current.filter((code) => code !== moduleCode) : [...current, moduleCode],
    )
  }

  const handleSave = async () => {
    setIsSaving(true)

    try {
      const response = await handleUpdateModules(pendingDisabled)

      if (!response.success) {
        setError(t("companyModules.errorTitle"), response.message)

        return
      }

      await refresh()

      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay} style={{ zIndex: 940 }}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert1} min-w-0 max-h-[85vh] overflow-y-auto`}>
            <TitlePageResponsiveIconSmall
              title={t("companyModules.title")}
              icon={faSliders}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.company-modules"
                    variant="global"
                    title={t("helpPopup:global.companyModules.title")}
                    description={t("helpPopup:global.companyModules.description")}
                    bullets={t("helpPopup:global.companyModules.bullets", { returnObjects: true }) as string[]}
                    steps={t("helpPopup:global.companyModules.steps", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyModules.companyModulesPage.button.onClose"
                  />
                </div>
              }
            />

            <div className="mt-4 space-y-2 p-1">
              {MODULE_DISPLAY_GROUPS.map((group) => (
                <div key={group.titleKey}>
                  <div className="space-y-1">
                    {group.modules.map((moduleCode) => {
                      const enabled = isPendingEnabled(moduleCode)

                      const lockedBy = lockedByFor(moduleCode)

                      const isLocked = enabled && lockedBy.length > 0

                      return (
                        <div key={moduleCode}>
                          <SimpleToggle
                            title={t(`companyModules.modules.${moduleCode}.title`)}
                            value={enabled}
                            disabled={isLocked || isLoading || isSaving}
                            onChange={() => handleToggle(moduleCode)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyModules.companyModulesPage.button.toggleModule"
                          />

                          {isLocked && (
                            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                              <p className="mt-1 px-1 text-xs font-semibold text-amber-600">
                                {t("companyModules.lockedBy", { modules: lockedBy.map(describeLock).join(", ") })}
                              </p>
                            </Animate>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <section className={defaultStyle.buttonsController}>
              <Button
                label={t("companyModules.save")}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                disabled={isSaving}
                onClick={() => void handleSave()}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.companyModules.companyModulesPage.button.save"
              />
            </section>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
